import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { CATEGORIES } from "../../constants/data";
import { Bunting, Sparkle, Heart, Star, Gift } from "../doodles/Doodles";
import useCategoryImages from "../../hooks/useCategoryImages";
import TileImage from "../shared/TileImage";

const TILE_COLORS = {
  her: "#FFE7F1",
  him: "#E7F4FF",
  kids: "#EAFBE7",
  babies: "#FCE4FF",
  baskets: "#FFF7DC",
};

// Home Hero tiles order — Diwali excluded because it has its own dedicated section
const HERO_ORDER = ["her", "him", "kids", "babies", "baskets"];

// Always return exactly 4 image URLs, padding with the default if needed
function getFourImages(slug, overridesAll, fallback) {
  const arr = (overridesAll && overridesAll[slug]) || [];
  const filled = [...arr];
  while (filled.length < 4) filled.push(fallback);
  return filled.slice(0, 4);
}

export default function Hero() {
  const overrides = useCategoryImages();
  const heroBg = overrides["hero-bg"] || "";
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const tiles = HERO_ORDER
    .map((slug) => CATEGORIES.find((c) => c.slug === slug))
    .filter(Boolean);

  // Auto-rotate every 5s (pauses on hover / touch)
  useEffect(() => {
    if (tiles.length <= 1 || paused) return;
    const t = setInterval(() => setActive((i) => (i + 1) % tiles.length), 5000);
    return () => clearInterval(t);
  }, [tiles.length, paused]);

  // Smooth-scroll the active card into view
  useEffect(() => {
    const el = cardRefs.current[active];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [active]);

  const go = (dir) => setActive((i) => (i + dir + tiles.length) % tiles.length);

  return (
    <section className="relative pt-24 pb-8 overflow-hidden" data-testid="hero">
      {/* Optional custom hero background image (falls back to pink gradient) */}
      {heroBg && (
        <div className="absolute inset-0 -z-10" data-testid="hero-bg-image" aria-hidden="true">
          <img src={heroBg} alt="" className="w-full h-full object-cover"/>
          {/* Cream/pink veil so text + doodles remain readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFFAF3]/70 via-[#FFE7F1]/55 to-[#FFFAF3]/85" />
        </div>
      )}
      <Bunting className="absolute top-[88px] left-6 w-44 sm:w-56 animate-sway pointer-events-none opacity-70"/>

      {/* Floating gift drawings */}
      <Gift color="#FF6B9D" className="absolute top-28 left-[6%] w-9 sm:w-11 animate-float-slow pointer-events-none" data-testid="hero-gift-1"/>
      <Gift color="#FFD93D" className="absolute top-32 right-[8%] w-8 sm:w-10 animate-float-fast pointer-events-none" style={{ animationDelay: "0.5s" }} data-testid="hero-gift-2"/>
      <Gift color="#BAE1FF" className="absolute bottom-6 left-[4%] w-10 sm:w-12 animate-float-slow pointer-events-none hidden sm:block" style={{ animationDelay: "1s" }} data-testid="hero-gift-3"/>
      <Gift color="#C8E6C9" className="absolute bottom-8 right-[4%] w-9 sm:w-11 animate-float-fast pointer-events-none hidden sm:block" style={{ animationDelay: "1.5s" }} data-testid="hero-gift-4"/>

      {/* Sparse floating decorations */}
      <Sparkle color="#FFD93D" className="absolute top-28 right-1/3 w-5 animate-twinkle pointer-events-none"/>
      <Sparkle color="#FF6B9D" className="absolute bottom-12 left-1/3 w-4 animate-twinkle pointer-events-none" style={{ animationDelay: "0.8s" }}/>
      <Heart color="#FF6B9D" className="absolute top-1/3 right-[24%] w-5 animate-bounce-doodle pointer-events-none"/>
      <Star color="#BAE1FF" className="absolute bottom-10 right-1/3 w-6 animate-spin-slow pointer-events-none hidden lg:block"/>

      <div className="container-x pt-4 lg:pt-6 text-center relative">
        <p className="label-small text-xs sm:text-sm">✨ Thoughtful Gifts for Every Celebration ✨</p>

        {/* Single-row swipeable + auto-rotating category tiles */}
        <div
          className="mt-6 relative"
          data-testid="hero-tiles-row"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Left arrow */}
          {tiles.length > 1 && (
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid="hero-tiles-prev"
              aria-label="scroll left"
              className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] items-center justify-center"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2}/>
            </button>
          )}

          {/* Tiles scroller */}
          <div
            ref={scrollerRef}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setTimeout(() => setPaused(false), 2000)}
            className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 sm:px-12"
            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
          >
            {tiles.map((cat, i) => {
              const useGrid = cat.slug === "baskets";
              const four = getFourImages(cat.slug, overrides.all, cat.image);
              return (
                <Link
                  key={cat.slug}
                  to={`/shop/${cat.slug}`}
                  ref={(el) => (cardRefs.current[i] = el)}
                  data-tile
                  data-testid={`hero-category-${cat.slug}`}
                  className={`group doodle-card overflow-hidden flex flex-col snap-center shrink-0 w-[70%] sm:w-[42%] md:w-[30%] lg:w-[22%] transition-transform ${i === active ? "scale-[1.02]" : "scale-100"}`}
                  style={{ backgroundColor: TILE_COLORS[cat.slug] || "#FFE7F1" }}
                >
                  {useGrid ? (
                    /* 2x2 image grid (Baskets only) */
                    <div className="grid grid-cols-2 gap-px bg-white/40 aspect-square">
                      {four.map((src, k) => (
                        <div key={k} className="overflow-hidden bg-white/40">
                          <TileImage
                            src={overrides.all && overrides.all[cat.slug] && overrides.all[cat.slug][k]}
                            loaded={overrides.loaded}
                            fallback={cat.image}
                            alt={`${cat.name} ${k + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Single image */
                    <div className="aspect-square overflow-hidden bg-white/40">
                      <TileImage
                        src={overrides[cat.slug]}
                        loaded={overrides.loaded}
                        fallback={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-2.5 sm:p-3">
                    <div className="flex items-center justify-between gap-1">
                      <h3 className="font-doodle text-sm sm:text-base lg:text-lg text-[#2D2424] leading-tight">{cat.name}</h3>
                      <ArrowUpRight className="w-4 h-4 text-[#7A1F2B] group-hover:rotate-45 transition-transform shrink-0" strokeWidth={2}/>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Right arrow */}
          {tiles.length > 1 && (
            <button
              type="button"
              onClick={() => go(1)}
              data-testid="hero-tiles-next"
              aria-label="scroll right"
              className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full border-2 border-[#2D2424] bg-white hover:bg-[#FFE7F1] items-center justify-center"
              style={{ boxShadow: "3px 3px 0 #2D2424" }}
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2}/>
            </button>
          )}

          {/* Dots */}
          {tiles.length > 1 && (
            <div className="mt-5 flex justify-center gap-2" data-testid="hero-tiles-dots">
              {tiles.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`hero-tiles-dot-${i}`}
                  aria-label={`go to category ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === active ? "w-8 bg-[#7A1F2B]" : "w-2 bg-[#7A1F2B]/30 hover:bg-[#7A1F2B]/60"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hide scrollbar */}
      <style>{`
        [data-testid='hero-tiles-row'] > div[class*='overflow-x-auto']::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}
